import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db, auth } from "../../../firebase/firebaseConfig";
import {
  FiBell,
  FiCheckCircle,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiMessageSquare,
  FiPlus,
  FiSearch,
  FiSend,
  FiTrash2,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { logClassActivity } from "../api/activityLogger";

const DEFAULT_FORM = {
  title: "",
  message: "",
  type: "announcement",
  audience: "all",
  studentIds: [],
  pinned: false,
  published: true,
};

const typeOptions = [
  { value: "announcement", label: "Thông báo chung" },
  { value: "reminder", label: "Nhắc nhở" },
  { value: "homework", label: "Bài tập" },
  { value: "event", label: "Sự kiện" },
];

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return new Date(ts > 1e12 ? ts : ts * 1000);
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
    if (typeof ts.toDate === "function") return ts.toDate();
  }
  return null;
}

function formatDate(ts) {
  const d = tsToDate(ts);
  return d ? d.toLocaleString("vi-VN") : "—";
}

function getStudentName(student) {
  return (
    student?.display?.name ||
    student?.name ||
    `${student?.firstName || ""} ${student?.lastName || ""}`.trim() ||
    student?.display?.email ||
    student?.email ||
    student?.id ||
    "Học viên"
  );
}

function getTypeLabel(type) {
  return typeOptions.find((item) => item.value === type)?.label || "Thông báo";
}

function NotificationForm({ initialValue, students, onCancel, onSubmit, saving }) {
  const [form, setForm] = useState(initialValue || DEFAULT_FORM);

  useEffect(() => {
    setForm(initialValue || DEFAULT_FORM);
  }, [initialValue]);

  const selectedCount = Array.isArray(form.studentIds) ? form.studentIds.length : 0;

  const toggleStudent = (id) => {
    setForm((prev) => {
      const current = new Set(prev.studentIds || []);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      return { ...prev, studentIds: Array.from(current) };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      title: form.title.trim(),
      message: form.message.trim(),
      studentIds: form.audience === "selected" ? form.studentIds || [] : [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border bg-slate-50 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-bold text-slate-900">
          {initialValue?.id ? "Chỉnh sửa thông báo" : "Tạo thông báo mới"}
        </h3>
        <button type="button" onClick={onCancel} className="rounded-xl p-2 hover:bg-white text-slate-500">
          <FiX />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tiêu đề</label>
          <input
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full rounded-xl border bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-100"
            placeholder="Ví dụ: Nhắc lịch kiểm tra cuối tuần"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Loại thông báo</label>
          <select
            value={form.type}
            onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
            className="w-full rounded-xl border bg-white px-3 py-2"
          >
            {typeOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Nội dung</label>
        <textarea
          value={form.message}
          onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
          className="min-h-[120px] w-full rounded-xl border bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-100"
          placeholder="Nhập nội dung gửi đến học sinh..."
          required
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Đối tượng nhận</label>
          <select
            value={form.audience}
            onChange={(e) => setForm((prev) => ({ ...prev, audience: e.target.value }))}
            className="w-full rounded-xl border bg-white px-3 py-2"
          >
            <option value="all">Tất cả học sinh trong lớp</option>
            <option value="selected">Chọn học sinh cụ thể</option>
          </select>
        </div>

        <div className="flex items-end gap-4">
          <label className="inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.pinned}
              onChange={(e) => setForm((prev) => ({ ...prev, pinned: e.target.checked }))}
            />
            Ghim lên đầu
          </label>
          <label className="inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.published}
              onChange={(e) => setForm((prev) => ({ ...prev, published: e.target.checked }))}
            />
            Hiển thị ngay
          </label>
        </div>
      </div>

      {form.audience === "selected" && (
        <div className="rounded-xl border bg-white p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-700">Chọn học sinh nhận thông báo</div>
            <div className="text-xs text-slate-500">Đã chọn: {selectedCount}</div>
          </div>
          <div className="max-h-52 overflow-y-auto grid sm:grid-cols-2 gap-2 pr-1">
            {(students || []).map((student) => (
              <label key={student.id} className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={(form.studentIds || []).includes(student.id)}
                  onChange={() => toggleStudent(student.id)}
                />
                <span className="truncate">{getStudentName(student)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-xl border bg-white px-4 py-2 font-semibold hover:bg-slate-50">
          Hủy
        </button>
        <button
          type="submit"
          disabled={saving || !form.title.trim() || !form.message.trim() || (form.audience === "selected" && selectedCount === 0)}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <FiSend /> {saving ? "Đang lưu..." : initialValue?.id ? "Cập nhật" : "Gửi thông báo"}
        </button>
      </div>
    </form>
  );
}

export default function TabNotifications({ classInfo, students = [] }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [readCounts, setReadCounts] = useState({});

  useEffect(() => {
    if (!classInfo?.id) return undefined;
    setLoading(true);

    const qRef = query(
      collection(db, "class_notifications"),
      where("classId", "==", classInfo.id),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );

    return () => unsub && unsub();
  }, [classInfo?.id]);

 useEffect(() => {
  if (!notifications.length) {
    setReadCounts({});
    return undefined;
  }

  const unsubscribes = notifications.map((item) => {
    const readsRef = collection(db, "class_notifications", item.id, "reads");

    return onSnapshot(
      readsRef,
      (snap) => {
        setReadCounts((prev) => ({
          ...prev,
          [item.id]: snap.size,
        }));
      },
      (err) => {
        console.error("Cannot listen notification reads", item.id, err);
      }
    );
  });

  return () => unsubscribes.forEach((u) => u && u());
}, [notifications]);

  const stats = useMemo(() => {
    const published = notifications.filter((n) => n.published !== false).length;
    const draft = notifications.filter((n) => n.published === false).length;
    const pinned = notifications.filter((n) => !!n.pinned).length;
    return { total: notifications.length, published, draft, pinned };
  }, [notifications]);

  const filtered = useMemo(() => {
    return notifications
      .filter((item) => {
        const okSearch = !search || `${item.title || ""} ${item.message || ""}`.toLowerCase().includes(search.toLowerCase());
        const okFilter =
          filter === "all" ||
          (filter === "published" && item.published !== false) ||
          (filter === "draft" && item.published === false) ||
          (filter === "pinned" && item.pinned);
        return okSearch && okFilter;
      })
      .sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || (tsToDate(b.createdAt)?.getTime() || 0) - (tsToDate(a.createdAt)?.getTime() || 0));
  }, [notifications, search, filter]);

  const handleCreateOrUpdate = async (data) => {
    setSaving(true);
    try {
      const user = auth.currentUser;
      const payload = {
        ...data,
        classId: classInfo.id,
        className: classInfo.name || "",
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
        updatedByName: user?.displayName || user?.email || "Giáo viên",
      };

      if (editing?.id) {
        await updateDoc(doc(db, "class_notifications", editing.id), payload);
        await logClassActivity({
          classId: classInfo.id,
          type: "notification_updated",
          severity: data.published ? "normal" : "warning",
          targetId: editing.id,
          targetTitle: data.title,
          message: `Đã cập nhật thông báo: ${data.title}.`,
          metadata: { notificationId: editing.id, audience: data.audience, targetCount: data.audience === "selected" ? data.studentIds.length : students.length },
        });
      } else {
        const notificationRef = await addDoc(collection(db, "class_notifications"), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: user?.uid || null,
          createdByName: user?.displayName || user?.email || "Giáo viên",
          readBy: {},
        });
        await logClassActivity({
          classId: classInfo.id,
          type: "notification_created",
          severity: data.published ? "normal" : "warning",
          targetId: notificationRef.id,
          targetTitle: data.title,
          message: `${data.published ? "Đã đăng" : "Đã lưu bản nháp"} thông báo: ${data.title}.`,
          metadata: { notificationId: notificationRef.id, audience: data.audience, targetCount: data.audience === "selected" ? data.studentIds.length : students.length },
        });
      }

      setShowForm(false);
      setEditing(null);
    } catch (err) {
      alert("Lỗi lưu thông báo: " + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditing({
      id: item.id,
      title: item.title || "",
      message: item.message || "",
      type: item.type || "announcement",
      audience: item.audience || "all",
      studentIds: item.studentIds || [],
      pinned: !!item.pinned,
      published: item.published !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Xóa thông báo này?")) return;
    await deleteDoc(doc(db, "class_notifications", id));
  };

  const handleTogglePublished = async (item) => {
    await updateDoc(doc(db, "class_notifications", item.id), {
      published: item.published === false,
      updatedAt: serverTimestamp(),
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Thông báo lớp học</h2>
          <p className="mt-1 text-sm text-slate-600">Gửi thông báo, nhắc lịch và ghi chú quan trọng cho học sinh trong lớp.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
        >
          <FiPlus /> Tạo thông báo
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-blue-700">{stats.total}</div>
          <div className="text-xs text-slate-500">Tổng</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-emerald-600">{stats.published}</div>
          <div className="text-xs text-slate-500">Đang hiển thị</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-amber-600">{stats.draft}</div>
          <div className="text-xs text-slate-500">Bản nháp</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-purple-600">{stats.pinned}</div>
          <div className="text-xs text-slate-500">Đã ghim</div>
        </div>
      </div>

      {showForm && (
        <NotificationForm
          initialValue={editing || DEFAULT_FORM}
          students={students}
          saving={saving}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={handleCreateOrUpdate}
        />
      )}

      <div className="rounded-2xl border bg-white p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border bg-white py-2 pl-9 pr-3 outline-none focus:ring-2 focus:ring-blue-100"
              placeholder="Tìm theo tiêu đề hoặc nội dung..."
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-xl border bg-white px-3 py-2"
          >
            <option value="all">Tất cả</option>
            <option value="published">Đang hiển thị</option>
            <option value="draft">Bản nháp</option>
            <option value="pinned">Đã ghim</option>
          </select>
        </div>

        {loading ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">Đang tải thông báo...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">Chưa có thông báo phù hợp.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => {
              const readCount = readCounts[item.id] || 0;
              const targetCount = item.audience === "selected" ? (item.studentIds || []).length : students.length;
              return (
                <article key={item.id} className="rounded-2xl border bg-white p-4 shadow-sm hover:shadow-md transition">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                          <FiBell /> {getTypeLabel(item.type)}
                        </span>
                        {item.pinned && <span className="rounded-lg border border-purple-100 bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700">Đã ghim</span>}
                        <span className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${item.published === false ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                          {item.published === false ? "Bản nháp" : "Đang hiển thị"}
                        </span>
                      </div>
                      <h3 className="mt-2 text-lg font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{item.message}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
                        <span>{formatDate(item.createdAt)}</span>
                        <span className="inline-flex items-center gap-1"><FiUsers /> {item.audience === "selected" ? `${targetCount} học sinh được chọn` : "Cả lớp"}</span>
                        <span className="inline-flex items-center gap-1"><FiCheckCircle /> Đã đọc: {readCount}/{targetCount || 0}</span>
                      </div>
                    </div>
                    <div className="flex flex-row lg:flex-col gap-2 lg:w-36 shrink-0">
                      <button type="button" onClick={() => handleEdit(item)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                        <FiEdit2 /> Sửa
                      </button>
                      <button type="button" onClick={() => handleTogglePublished(item)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                        {item.published === false ? <FiEye /> : <FiEyeOff />} {item.published === false ? "Hiện" : "Ẩn"}
                      </button>
                      <button type="button" onClick={() => handleDelete(item.id)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                        <FiTrash2 /> Xóa
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <div className="font-semibold flex items-center gap-2"><FiMessageSquare /> Gợi ý sử dụng</div>
        <p className="mt-1">Thông báo có trạng thái “Đang hiển thị” sẽ xuất hiện ở tab Thông báo của học sinh. Học sinh có thể đánh dấu đã đọc để giáo viên theo dõi.</p>
      </div>
    </div>
  );
}
